import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import MainService from '../services/MainService';

const MyAddress = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newAddress, setNewAddress] = useState({
    Location: '',
    Address: '',
    City: '',
    POBox: '',
    Type: ''
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const customerId = user.CustomerId ;

  useEffect(() => {
    loadAddresses();
  }, []);

  const loadAddresses = async () => {
    if (!customerId) {
      setError('Customer ID not found. Please login again.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await MainService.getCustomerAddresses(customerId);
      if (response && Array.isArray(response)) {
        setAddresses(response);
      } else {
        setAddresses([]);
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
      setError('Failed to load addresses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddress = async () => {
    if (!customerId) {
      setError('Customer ID not found. Please login again.');
      return;
    }

    // Validation
    if (!newAddress.Location || !newAddress.Address || !newAddress.City || !newAddress.Type) {
      setError('Please fill in all required fields (Location, Address, City, Type)');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const addressData = {
        CustomerId: customerId,
        Location: newAddress.Location,
        Address: newAddress.Address,
        City: newAddress.City,
        POBox: newAddress.POBox,
        Type: newAddress.Type
      };

      const response = await MainService.AddCusAddress(addressData);
      
      if (response === true || response === 1) {
        setSuccess('Address added successfully!');
        setOpenModal(false);
        setNewAddress({
          Location: '',
          Address: '',
          City: '',
          POBox: '',
          Type: ''
        });
        // Refresh the address list
        await loadAddresses();
      } else {
        setError('Failed to add address. Please try again.');
      }
    } catch (error) {
      console.error('Error adding address:', error);
      setError('Failed to add address. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setNewAddress({
      ...newAddress,
      [field]: event.target.value
    });
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'home':
        return <HomeIcon />;
      case 'business':
        return <BusinessIcon />;
      default:
        return <LocationIcon />;
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'home':
        return '#4CAF50';
      case 'business':
        return '#2196F3';
      default:
        return '#FF9800';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          My Addresses
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
          sx={{
            backgroundColor: '#2E7D32',
            '&:hover': {
              backgroundColor: '#1B5E20',
            },
          }}
        >
          Add Address
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : addresses.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <LocationIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No addresses found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add your first address to get started
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {addresses.map((address, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getTypeIcon(address.Type)}
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {address.Location}
                      </Typography>
                    </Box>
                    <Chip
                      label={address.Type}
                      size="small"
                      sx={{
                        backgroundColor: getTypeColor(address.Type),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                  
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {address.Address}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {address.City}
                  </Typography>
                  
                  {address.POBox && (
                    <Typography variant="body2" color="text.secondary">
                      PO Box: {address.POBox}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add Address Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Add New Address
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location Name"
                value={newAddress.Location}
                onChange={handleInputChange('Location')}
                placeholder="e.g., Home, Office, Villa"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={newAddress.Address}
                onChange={handleInputChange('Address')}
                multiline
                rows={3}
                placeholder="Enter your full address"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={newAddress.City}
                onChange={handleInputChange('City')}
                placeholder="Enter city name"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PO Box (Optional)"
                value={newAddress.POBox}
                onChange={handleInputChange('POBox')}
                placeholder="Enter PO Box if applicable"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Address Type</InputLabel>
                <Select
                  value={newAddress.Type}
                  onChange={handleInputChange('Type')}
                  label="Address Type"
                >
                  <MenuItem value="Home">Home</MenuItem>
                  <MenuItem value="Business">Business</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenModal(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddAddress}
            disabled={loading}
            sx={{
              backgroundColor: '#2E7D32',
              '&:hover': {
                backgroundColor: '#1B5E20',
              },
            }}
          >
            {loading ? <CircularProgress size={20} /> : 'Add Address'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyAddress; 